"use client";

import { useEffect, useState, Suspense } from "react";
import { useSearchParams } from "next/navigation";
import Image from "next/image";
import Link from "next/link";
import { storeLinks } from "@/components/sections/app-download";
import { useGift, useRevealMutation } from "@/hooks/use-gift";
import birthday from "@/public/animation/lottie_birthday.json";
import confetti from "@/public/animation/lottie_confetti.json";
import fireworks from "@/public/animation/lottie_fireworks.json";
import giftBox from "@/public/animation/lottie_gift.json";
import Lottie from "lottie-react";

function PageSkeleton() {
  return (
    <div className="flex items-center justify-center min-h-screen px-4 bg-gradient-to-b from-black via-slate-950 to-slate-900">
      <div className="w-full max-w-xl p-6 space-y-4 border global-border-radius border-slate-800 bg-slate-900/70 backdrop-blur sm:p-8 animate-pulse">
        <div className="w-40 h-6 rounded bg-slate-700" />
        <div className="w-64 h-4 rounded bg-slate-700" />
        <div className="w-full h-10 rounded bg-slate-700" />
        <div className="w-full h-10 rounded bg-slate-700" />
        <div className="w-full h-12 rounded bg-slate-700" />
      </div>
    </div>
  );
}

export default function GiftAppDownloadPage() {
  return (
    <Suspense fallback={<PageSkeleton />}>
      <GiftAppDownload />
    </Suspense>
  );
}

function GiftAppDownload() {
  const searchParams = useSearchParams();
  const token = searchParams.get("token") ?? undefined;
  const [showIntro, setShowIntro] = useState(true);
  const [opened, setOpened] = useState(false);
  const [revealResult, setRevealResult] = useState<any>(null);
  const { data: gift, isLoading, isSuccess } = useGift(token);
  const { mutateAsync, isPending } = useRevealMutation();

  const handleReveal = async () => {
    if (!token) return;

    try {
      const res = await mutateAsync({
        reveal: true,
        token,
      });

      setRevealResult(res.data);
      setOpened(true);
    } catch (err) {
      console.error(err);
    }
  };

  const animationMap: Record<string, any> = {
    fireworks,
    confetti,
    gift: giftBox,
    birthday,
  };
  const introAnimation = animationMap[gift?.gift_content as string] ?? fireworks;
  const [isRevealed, setIsRevealed] = useState(false);
  const description = gift?.gift_video_url
    ? "Scan the QR code to download the app, or tap a store badge below to install it and watch your video gift come to life!"
    : "Scan the QR code to download the app, or tap a store badge below to install it and reveal your gift.";

  useEffect(() => {
    if (isSuccess) {
      const timer = setTimeout(() => setIsRevealed(true), 1200);

      return () => clearTimeout(timer);
    }
  }, [isSuccess]);

  return (
    <>
      {!opened && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center min-h-screen gap-8 text-center bg-black/80 backdrop-blur-sm bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
          <h1 className="mb-6 text-4xl font-bold text-white">You’ve Received a Gift! </h1>

          <button
            onClick={handleReveal}
            disabled={isPending}
            className="transition transform hover:animate-bounce text-7xl hover:scale-150"
          >
            🎁{" "}
          </button>

          <p className="mt-4 text-slate-400">Tap the gift box to reveal your surprise</p>
        </div>
      )}
      {opened && revealResult?.data.gift_reveal_type === "animation" && showIntro && (
        <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm bg-black/80 ">
          <div className="relative w-[420px] h-[420px]">

            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-[640px]">
                <Lottie
                  animationData={introAnimation}
                  loop={false}
                  onComplete={() => {
                    setTimeout(() => {
                      setShowIntro(false);
                    }, 500);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      {isLoading || !gift ? (
        <FallbackPage />
      ) : (
        <div className="flex flex-col items-center justify-center min-h-screen px-4 text-center bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
          <h1 className="mb-2 text-4xl font-bold text-white">
            🎁 A Gift for {gift.gift_receive_name}!
          </h1>
          <p className="max-w-md mb-2 text-lg text-slate-300">
            {gift.gift_sender_name} sent you{" "}
            <span className="font-bold text-orange-500">
              {gift.to_currency} {gift.to_amount}.
            </span>
          </p>
          <div className="max-w-md p-4 mt-4 mb-6 border border-orange-500/30 bg-orange-500/10 global-border-radius">
            <p className="text-lg italic text-orange-500">“{gift?.gift_message}”</p>
          </div>
          <p className="max-w-md text-sm text-slate-400">{description}</p>

          <div className="flex flex-col items-center gap-4">
            <div className="relative mx-auto aspect-square w-full max-w-[170px] overflow-hidden global-border-radius bg-white p-2 mb-5 mt-5">
              <Image
                src="/QR-Download.png"
                alt="QR code to download the app"
                fill
                className="object-contain"
                priority
                fetchPriority="high"
              />
            </div>

            <div className="space-y-4 sm:space-y-5">
              <div className="flex max-sm:flex-wrap justify-center">
                {storeLinks.map((link) => (
                  <Link
                    key={link.name}
                    href={link.href}
                    className="
                      group 
                      inline-flex 
                      items-center 
                      justify-center 
                      global-border-radius 
                      px-2 
                      py-1 
                      transition 
                      hover:-translate-y-0.5 
                      focus-visible:outline 
                      focus-visible:outline-2 
                      focus-visible:outline-offset-2 
                      focus-visible:outline-orange-400
                    "
                    aria-label={`Open ${link.name}`}
                  >
                    <Image
                      src={link.src}
                      alt={link.name}
                      width={150}
                      height={45}
                      className="object-contain w-full h-full"
                      style={{ width: "140px", height: "45px" }}
                    />
                  </Link>
                ))}
              </div>
              <p className="text-xs text-center text-gray-600">
                iOS · Android · Huawei AppGallery. One scan to install and start.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function FallbackPage() {
  return (
    <div className="flex flex-col items-center justify-center min-h-screen px-4 text-center bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      <h1 className="mb-4 text-4xl font-bold text-white">Vell App</h1>
      <p className="max-w-md mb-6 text-lg text-slate-300">
        Point your camera to open the link, or tap a store badge. Built for quick, secure transfers.
      </p>

      <div className="flex flex-col items-center gap-4">
        <div className="relative mx-auto aspect-square w-full max-w-[170px] overflow-hidden global-border-radius bg-white p-2 mb-5 mt-5">
          <Image
            src="/QR-Download.png"
            alt="QR code to download the app"
            fill
            className="object-contain"
            priority
            fetchPriority="high"
          />
        </div>

        <div className="space-y-4 sm:space-y-5">
          <div className="flex max-sm:flex-wrap items-center gap-3 mb-12">
            {storeLinks.map((link) => (
              <Link
                key={link.name}
                href={link.href}
                className="
                  group 
                  inline-flex 
                  items-center 
                  justify-center 
                  global-border-radius 
                  px-2 
                  py-1 
                  transition 
                  hover:-translate-y-0.5 
                  focus-visible:outline 
                  focus-visible:outline-2 
                  focus-visible:outline-offset-2 
                  focus-visible:outline-orange-400
                "
                aria-label={`Open ${link.name}`}
              >
                <Image
                  src={link.src}
                  alt={link.name}
                  width={150}
                  height={45}
                  className="object-contain w-full h-full"
                  style={{ width: "140px", height: "45px" }}
                />
              </Link>
            ))}
          </div>
          <p className="text-xs text-center text-gray-600">
            iOS · Android · Huawei AppGallery. One scan to install and start.
          </p>
        </div>
      </div>
    </div>
  );
}
